import { useState } from 'react';

import {
  Box,
  Button,
  Collapse,
  Radio,
  RadioGroup,
  SvgIcon,
  Typography,
} from '@material-ui/core';

import {
  RiArrowUpSLine as ExpandLess,
  RiArrowDownSLine as ExpandMore,
} from '@remixicon/react';

import {
  ConfidenceWebsiteLink,
  DialogCompositeMui as DialogComposite,
  FormControlCardLabelMui as FormControlCardLabel,
  MultiStepDialogMui as MultiStepDialog,
  useFormField,
  useMultiStepDialogContext,
} from '@spotify/confidence-core-react';

import {
  METRIC_TYPE_CUSTOM_OPTIONS,
  METRIC_TYPE_TEMPLATE_OPTIONS,
  MetricType,
  MetricTypeOption,
  defaultSpecs,
} from '../../metricTypes';
import { ResourceType } from '../forms';
import { MetricFormValues, StepId } from '../types';

type TypeStepProps = {
  stepId?: string;
  templateOptions?: MetricTypeOption[];
  customOptions?: MetricTypeOption[];
  resourceType?: ResourceType;
};

export const TypeStep = ({
  stepId = StepId.Type,
  templateOptions = METRIC_TYPE_TEMPLATE_OPTIONS,
  customOptions = METRIC_TYPE_CUSTOM_OPTIONS,
  resourceType = 'metric',
}: TypeStepProps) => {
  const { activeStep } = useMultiStepDialogContext();
  const isFirstStep = activeStep === 0;
  const [showCustom, setShowCustom] = useState(false);

  const { value: metricType, setValue: setMetricType } = useFormField<
    MetricFormValues,
    'metricType'
  >('metricType');

  const { setValue: setTypeSpec } = useFormField<MetricFormValues, 'typeSpec'>(
    'typeSpec',
  );

  const handleSelectType = (newType: MetricType) => {
    if (newType !== metricType) {
      setMetricType(newType);
      // Update typeSpec to match the new metric type
      setTypeSpec(defaultSpecs[newType]);
    }
  };

  const renderTypeOption = (option: MetricTypeOption) => (
    <FormControlCardLabel
      key={option.value}
      value={option.value}
      variant="outlined"
      control={<Radio color="primary" />}
      label={option.title}
      description={option.description}
      icon={option.icon as typeof SvgIcon}
      expandedContent={option.expandedContent}
    />
  );

  const canProceed = metricType && metricType !== 'unknown';

  return (
    <MultiStepDialog.Step id={stepId}>
      <MultiStepDialog.Header>
        <Typography variant="h4" component="h4">
          Type
        </Typography>
        <DialogComposite.Subtitle>
          Select the kind of {resourceType} you would like to create.{' '}
          <ConfidenceWebsiteLink route="/docs/metrics" underline="always" data-track-source-anchor="2da2d1d80e">
            Learn more.
          </ConfidenceWebsiteLink>
        </DialogComposite.Subtitle>
      </MultiStepDialog.Header>
      <MultiStepDialog.Body>
        <RadioGroup
          aria-label="type"
          name="metric-type"
          value={metricType || 'unknown'}
          onChange={e => handleSelectType(e.target.value as MetricType)}
         data-track-source-anchor="ad0a3fb34d">
          {templateOptions.map(renderTypeOption)}
          {customOptions.length > 0 && (
            <>
              <Box display="flex" alignItems="center" justifyContent="center">
                <Button
                  endIcon={showCustom ? <ExpandLess /> : <ExpandMore />}
                  onClick={() => setShowCustom(current => !current)}
                  data-testid="toggle-custom-types"
                 data-track-source-anchor="84abbc6faf">
                  Custom
                </Button>
              </Box>
              <Collapse in={showCustom}>
                {customOptions.map(renderTypeOption)}
              </Collapse>
            </>
          )}
        </RadioGroup>
      </MultiStepDialog.Body>
      <MultiStepDialog.Actions>
        {isFirstStep ? (
          <MultiStepDialog.Actions.Cancel />
        ) : (
          <MultiStepDialog.Actions.Prev />
        )}
        <MultiStepDialog.Actions.Next disabled={!canProceed} />
      </MultiStepDialog.Actions>
    </MultiStepDialog.Step>
  );
};
